import React from "react";
import classNames from "classnames";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { UseCases } from "@planzone/core";
import { project as defaultProject, resource, task } from "@planzone/dto";
import {
  useAuthentication,
  useRetrieveCurrentPlanzone,
  useRetrievePlanzoneSettings,
  useSaveTimeRecord,
} from "@planzone/view-models";
import { useModal } from "@root/client/modal";
import {
  usePlanzonePermissions,
  useProjectPermissions,
} from "@components/permissions";
import { MemberAvatar } from "@common/memberAvatar";
import { ZoneAvatar } from "@common/zoneAvatar";
import { EditIcon } from "@common/editIcon";
import { TrashIcon } from "@common/trashIcon";
import { SecondaryCriteriaForm } from "@common/secondaryCriteriaForm";
import { NumberInput } from "@common/numberInput";
import { AllocationUnit } from "@common/allocationUnit";
import {
  parseAsLocalDate,
  parseTimestamp,
  toFixed,
} from "@pages/sharedTimeSheetUtils";
import { TruncatedName } from "./truncatedName";
import { Props } from "./types";
import styles from "./styles.scss";

function Row({ timeRecord, columns, template, validationContext }: Props) {
  const { t } = useTranslation();
  const { pushView } = useModal();
  const { account } = useAuthentication();
  const { planzone } = useRetrieveCurrentPlanzone();
  const { settings } = useRetrievePlanzoneSettings(planzone);
  const { isAdministrator } = usePlanzonePermissions();
  const { isProjectManager } = useProjectPermissions({
    ...defaultProject,
    uuid: timeRecord.project.uuid,
  });
  const date = parseAsLocalDate(timeRecord.recordingDate);
  const integratedAt =
    validationContext === "project"
      ? timeRecord.project.integratedAt
      : timeRecord.resource.integratedAt;
  const isValidated = !!integratedAt && parseTimestamp(integratedAt) >= date;

  const isLocked =
    (planzone.timesheetDeadline != null &&
      date <= parseTimestamp(planzone.timesheetDeadline)) ||
    (validationContext === "resource" &&
      settings.lockBeforeIntegrationDate &&
      isValidated);

  const canEdit =
    !isLocked &&
    (isAdministrator ||
      (timeRecord.resource.uuid === account.resource.uuid &&
        settings.canEditPersonalTimeSheet) ||
      (isProjectManager && settings.canEditProjectPersonalTimeSheet) ||
      settings.canEditManagerPersonalTimeSheet);

  const record = {
    date: date.day,
    month: date.month,
    year: date.year,
    timeSpent: timeRecord.timeSpent,
    attributes: timeRecord.attributes,
    resource: {
      ...resource,
      uuid: timeRecord.resource.uuid,
      name: timeRecord.resource.name,
    },
    task: {
      ...task,
      uuid: timeRecord.task.uuid,
      name: timeRecord.task.name,
    },
  };
  const { isRequestPending, saveTimeRecord } = useSaveTimeRecord(record);
  const [timeSpent, setTimeSpent] = React.useState(timeRecord.timeSpent);

  React.useEffect(() => {
    setTimeSpent(timeRecord.timeSpent);
  }, [timeRecord.timeSpent]);

  const editTimeSpent = () => {
    const isValid = !isNaN(timeSpent) && timeSpent >= 0 && timeSpent <= 24;

    isValid && timeSpent !== timeRecord.timeSpent
      ? saveTimeRecord({ timeRecord: { ...record, timeSpent } })
      : setTimeSpent(timeRecord.timeSpent);
  };

  const edit = () =>
    pushView({
      key: UseCases.saveTimeRecord,
      data: { timeRecord: record, isDisabled: false },
    });

  const editCriteria = (criteriaUuid: string, value: string) =>
    value !== (timeRecord.attributes[criteriaUuid] || "") &&
    saveTimeRecord({
      timeRecord: {
        ...record,
        attributes: { ...timeRecord.attributes, [criteriaUuid]: value },
      },
    });

  const remove = () =>
    pushView({ key: UseCases.deleteTimeRecord, data: timeRecord });

  const fixedColMap: { [key: string]: () => React.ReactNode } = {
    date: () => (
      <p className={styles.text}>{date.toLocaleString(DateTime.DATE_SHORT)}</p>
    ),
    resourceTask: () => (
      <div className={styles.resourceTask}>
        <div className={styles.avatar}>
          <MemberAvatar avatarUrl="" name={timeRecord.resource.name} />
        </div>
        <div className={styles.names}>
          <p className={styles.resourceName}>{timeRecord.resource.name}</p>
          <TruncatedName
            name={timeRecord.task.name}
            className={styles.taskName}
          />
        </div>
      </div>
    ),
    project: () => (
      <div className={styles.project}>
        <div className={styles.zoneAvatar}>
          <ZoneAvatar
            name={timeRecord.zone.name}
            color={timeRecord.zone.color}
          />
        </div>
        <div className={styles.projectName}>
          <TruncatedName
            name={timeRecord.project.name}
            className={styles.text}
          />
        </div>
      </div>
    ),
    timeSpent: () =>
      canEdit ? (
        <form
          className={styles.timeSpentForm}
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            editTimeSpent();
          }}
        >
          <NumberInput
            className={styles.timeSpentInput}
            value={timeSpent}
            min={0}
            max={24}
            step={0.25}
            isDisabled={isRequestPending}
            input={(value) => setTimeSpent(value)}
            blur={editTimeSpent}
          />
          <span className={styles.unit}>
            <AllocationUnit />
          </span>
        </form>
      ) : (
        <p className={styles.timeSpent}>
          {toFixed(timeRecord.timeSpent)} <AllocationUnit />
        </p>
      ),
    validation: () => (
      <span
        className={classNames(styles.validation, {
          [styles.validated]: isValidated,
        })}
      >
        {t(
          isValidated
            ? "weekTimeRecords.validated"
            : "weekTimeRecords.toValidate",
        )}
      </span>
    ),
  };

  return (
    <tr className={styles.row}>
      {columns.map(({ key }) => {
        const criteria = template.find(({ uuid }) => uuid === key);

        return (
          <td key={key}>
            {fixedColMap[key] ? (
              fixedColMap[key]()
            ) : criteria ? (
              <SecondaryCriteriaForm
                criteria={criteria}
                value={timeRecord.attributes[criteria.uuid] || ""}
                flexible
                disabled={!canEdit || isRequestPending}
                submit={(value) => editCriteria(criteria.uuid, value)}
              />
            ) : null}
          </td>
        );
      })}
      <td>
        {canEdit && (
          <div className={styles.actions}>
            <button
              className={styles.action}
              title={t("weekTimeRecords.edit")}
              onClick={edit}
            >
              <EditIcon className={styles.icon} />
            </button>
            <button
              className={classNames(styles.action, styles.remove)}
              title={t("weekTimeRecords.remove")}
              onClick={remove}
            >
              <TrashIcon className={styles.icon} />
            </button>
          </div>
        )}
      </td>
    </tr>
  );
}

export { Row };
